"use client";

import { Kbd, KbdGroup } from "@workspace/ui/components/kbd";
import { KeyboardShortcut } from "@workspace/ui/components/keyboard-shortcut";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@workspace/ui/components/sidebar";
import { Skeleton } from "@workspace/ui/components/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import {
  BarChart,
  BookOpen,
  ChevronRight,
  ClipboardList,
  FileSpreadsheet,
  Import,
  Landmark,
  type LucideIcon,
  Package,
  PieChart,
  Plus,
  ScanBarcode,
  ShoppingCart,
  Sparkles,
} from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { useParams, usePathname, useRouter } from "next/navigation";
import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import {
  DEFAULT_NEW_SALE_PATH,
  type NewSaleDefaultPath,
} from "@/lib/constants/new-sale-default-path";
import { FEATURE_PERMISSIONS } from "@/lib/constants/permissions";
import { useSidebarListHrefReader } from "./list-return-provider";
import { ListReturnLink } from "./ui/list-return-link";
import type { SidebarNavItem } from "./navigation-config";

// Map string icon names to actual components
const iconMap: Record<string, LucideIcon> = {
  PieChart,
  Sparkles,
  ShoppingCart,
  Package,
  FileSpreadsheet,
  Import,
  ScanBarcode,
  ClipboardList,
  BarChart,
  Landmark,
  BookOpen,
};

const GO_TO_PREFIX_KEY = "g";
const SHORTCUT_RESET_MS = 1200;

function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.isContentEditable
  );
}

function SidebarTooltipContent({
  label,
  shortcut,
}: {
  label: string;
  shortcut?: string;
}) {
  if (!shortcut) {
    return label;
  }

  return (
    <span className="flex items-center gap-3">
      {label}
      <KbdGroup>
        <Kbd>G</Kbd>
        <span className="font-sans text-muted text-xs">luego</span>
        <Kbd>{shortcut}</Kbd>
      </KbdGroup>
    </span>
  );
}

function ShortcutSidebarMenuButton({
  children,
  shortcut,
  tooltipLabel,
  ...props
}: Omit<React.ComponentProps<typeof SidebarMenuButton>, "tooltip"> & {
  shortcut?: string;
  tooltipLabel: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger render={<SidebarMenuButton {...props} />}>
        {children}
      </TooltipTrigger>
      <TooltipContent align="center" side="right">
        <SidebarTooltipContent label={tooltipLabel} shortcut={shortcut} />
      </TooltipContent>
    </Tooltip>
  );
}

export function NavMain({
  items,
  userPermissions = [],
  defaultNewSalePath = DEFAULT_NEW_SALE_PATH,
  isLoading = false,
}: {
  items: SidebarNavItem[];
  userPermissions?: string[];
  defaultNewSalePath?: NewSaleDefaultPath;
  isLoading?: boolean;
}) {
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();
  const newSaleHref = `/${params.orgSlug}${defaultNewSalePath}` as Route;
  const pendingGoToShortcutRef = React.useRef(false);
  const readSidebarListHref = useSidebarListHrefReader();
  const [mobileExpandedItemUrl, setMobileExpandedItemUrl] = React.useState<
    string | null
  >(null);

  const handleNavigation = () => {
    setOpenMobile(false);
  };

  const handleMobileParentClick = (itemUrl: string) => {
    setMobileExpandedItemUrl((expandedItemUrl) =>
      expandedItemUrl === itemUrl ? null : itemUrl
    );
  };

  // Check if user has permission to create new sales
  const canCreateSale = userPermissions.includes(
    FEATURE_PERMISSIONS.SALES_WRITE
  );

  const isPathMatch = (targetPath: string) => {
    if (targetPath.endsWith("/")) {
      return pathname === targetPath;
    }

    return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
  };

  useHotkeys(
    "v",
    (event) => {
      if (!canCreateSale || pendingGoToShortcutRef.current) {
        return;
      }
      event.preventDefault();
      router.push(newSaleHref);
    },
    {
      enableOnFormTags: false,
    },
    [router, canCreateSale, newSaleHref]
  );

  const shortcutRoutes = React.useMemo(() => {
    const routes = new Map<string, Route>();

    for (const item of items) {
      if (item.shortcut && item.parentAccessible !== false) {
        routes.set(
          item.shortcut.toLowerCase(),
          `/${params.orgSlug}${item.href ?? item.url}` as Route
        );
      }
    }

    return routes;
  }, [items, params.orgSlug]);

  React.useEffect(() => {
    let resetTimeoutId: number | undefined;

    const resetPendingShortcut = () => {
      pendingGoToShortcutRef.current = false;
      if (resetTimeoutId) {
        window.clearTimeout(resetTimeoutId);
        resetTimeoutId = undefined;
      }
    };

    const armPendingShortcut = () => {
      pendingGoToShortcutRef.current = true;
      if (resetTimeoutId) {
        window.clearTimeout(resetTimeoutId);
      }
      resetTimeoutId = window.setTimeout(
        resetPendingShortcut,
        SHORTCUT_RESET_MS
      );
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.isComposing ||
        isEditableEventTarget(event.target)
      ) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === GO_TO_PREFIX_KEY) {
        armPendingShortcut();
        return;
      }

      if (!pendingGoToShortcutRef.current) {
        return;
      }

      const href = shortcutRoutes.get(key);
      resetPendingShortcut();

      if (!href) {
        return;
      }

      event.preventDefault();
      setOpenMobile(false);
      router.push(readSidebarListHref(href) as Route);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      resetPendingShortcut();
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [readSidebarListHref, router, setOpenMobile, shortcutRoutes]);

  const isParentActive = (item: (typeof items)[0]) => {
    const itemPath = `/${params.orgSlug}${item.url}`;
    if (!item.items) {
      if (item.url === "/") {
        return pathname === itemPath;
      }
      return isPathMatch(itemPath);
    }
    // For items with subitems, only active if exactly on parent path
    return pathname === itemPath;
  };

  const isSubItemActive = (subItemUrl: string) => isPathMatch(subItemUrl);

  const shouldShowSubItems = (item: (typeof items)[0]) => {
    if (!item.items) {
      return false;
    }
    const itemPath = `/${params.orgSlug}${item.url}`;
    if (isMobile && mobileExpandedItemUrl === item.url) {
      return true;
    }

    return (
      isPathMatch(itemPath) ||
      item.items.some((subItem) =>
        isPathMatch(`/${params.orgSlug}${subItem.url}`)
      )
    );
  };

  const isParentExpanded = (item: (typeof items)[0]) =>
    item.parentAccessible === false || shouldShowSubItems(item);

  if (isLoading) {
    return (
      <SidebarGroup>
        <SidebarGroupContent className="flex flex-col gap-2">
          <Skeleton className="h-8 w-full" />
          <SidebarMenu>
            {Array.from({ length: 8 }).map((_, index) => (
              <SidebarMenuItem key={index}>
                <SidebarMenuSkeleton showIcon suppressHydrationWarning />
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        {canCreateSale && (
          <SidebarMenu>
            <SidebarMenuItem className="flex items-center gap-2">
              <SidebarMenuButton
                className="inline-flex h-12 min-w-8 shrink-0 cursor-pointer gap-2 whitespace-nowrap rounded-lg border bg-background font-medium text-lg shadow-xs outline-none transition-all duration-200 ease-linear hover:bg-accent hover:text-accent-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 group-data-[collapsible=icon]:justify-center md:h-8 md:text-sm dark:border-input dark:bg-input/30 dark:aria-invalid:ring-destructive/40 dark:hover:bg-input/50 group-data-[collapsible=icon]:[&>span]:hidden [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
                render={<Link href={newSaleHref} onClick={handleNavigation} />}
                tooltip="Nueva Venta"
              >
                <Plus />
                <span className="flex items-center gap-1">
                  Nueva Venta
                  <span
                    className="absolute right-3 hidden group-data-[collapsible=icon]:hidden md:inline"
                    style={{ top: "50%", transform: "translateY(-50%)" }}
                  >
                    <KeyboardShortcut keyLabel="V" />
                  </span>
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
        <SidebarMenu>
          {items.map((item) =>
            item.items ? (
              <SidebarMenuItem key={item.title}>
                {item.parentAccessible === false ? (
                  <ShortcutSidebarMenuButton
                    className="cursor-default py-6 text-lg text-muted-foreground md:py-0 md:text-sm [&_svg]:text-muted-foreground/50"
                    tooltipLabel={item.title}
                  >
                    {item.icon &&
                      iconMap[item.icon] &&
                      React.createElement(
                        iconMap[item.icon] as React.ComponentType
                      )}
                    <span>{item.title}</span>
                    <ChevronRight className="!text-muted-foreground/50 rotate-90" />
                  </ShortcutSidebarMenuButton>
                ) : (
                  <ShortcutSidebarMenuButton
                    className="py-6 text-lg md:py-0 md:text-sm [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground"
                    data-testid={
                      item.url === "/productos"
                        ? "productos-nav-trigger"
                        : undefined
                    }
                    isActive={isParentActive(item)}
                    onClick={
                      isMobile
                        ? () => handleMobileParentClick(item.url)
                        : undefined
                    }
                    render={
                      isMobile ? undefined : (
                        <ListReturnLink
                          href={`/${params.orgSlug}${item.href ?? item.url}`}
                          variant="sidebar"
                          onClick={handleNavigation}
                        />
                      )
                    }
                    shortcut={item.shortcut}
                    tooltipLabel={item.title}
                  >
                    {item.icon &&
                      iconMap[item.icon] &&
                      React.createElement(
                        iconMap[item.icon] as React.ComponentType
                      )}
                    <span>{item.title}</span>
                    <ChevronRight
                      className={cn(
                        "ml-auto",
                        "!text-muted-foreground/70 transition-transform duration-200",
                        isParentExpanded(item) ? "rotate-90" : "rotate-0"
                      )}
                    />
                  </ShortcutSidebarMenuButton>
                )}
                {isParentExpanded(item) && (
                  <SidebarMenuSub>
                    {isMobile && item.parentAccessible !== false && (
                      <SidebarMenuSubItem key={`${item.title}-parent`}>
                        <SidebarMenuSubButton
                          className="py-6 text-lg md:py-0 md:text-sm"
                          data-testid={
                            item.url === "/productos"
                              ? "productos-nav-link"
                              : undefined
                          }
                          isActive={isSubItemActive(
                            `/${params.orgSlug}${item.url}`
                          )}
                          render={
                            <ListReturnLink
                              href={`/${params.orgSlug}${item.href ?? item.url}`}
                              variant="sidebar"
                              onClick={handleNavigation}
                            />
                          }
                        >
                          {item.title}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    )}
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          className="py-6 text-lg md:py-0 md:text-sm"
                          isActive={isSubItemActive(
                            `/${params.orgSlug}${subItem.url}`
                          )}
                          render={
                            <ListReturnLink
                              href={`/${params.orgSlug}${subItem.url}`}
                              variant="sidebar"
                              onClick={handleNavigation}
                            />
                          }
                        >
                          {subItem.title}
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
            ) : (
              <SidebarMenuItem key={item.title}>
                <ShortcutSidebarMenuButton
                  className="py-6 text-lg md:py-0 md:text-sm [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground"
                  isActive={isParentActive(item)}
                  render={
                    <ListReturnLink
                      href={`/${params.orgSlug}${item.href ?? item.url}`}
                      variant="sidebar"
                      onClick={handleNavigation}
                    />
                  }
                  shortcut={item.shortcut}
                  tooltipLabel={item.title}
                >
                  {item.icon &&
                    iconMap[item.icon] &&
                    React.createElement(
                      iconMap[item.icon] as React.ComponentType
                    )}
                  <span>{item.title}</span>
                </ShortcutSidebarMenuButton>
              </SidebarMenuItem>
            )
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
